import mmodWorkflow from 'mmodWorkflow'
const Mammoth = require('mammoth')
const QuillModule = require('quill')
const Quill = QuillModule?.default || QuillModule?.Quill || QuillModule

const WIKI_ID_PREFIX = 'wikiId'

export default class mmodSkWiki extends mmodWorkflow {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([/* put module specific dependencies here */], dependencies)
    cfg_schema = _.extend({
      workflow_server: { type: 'string', default: ':msvrWiki', description: 'Listener acting as the server-side for the workflow' },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
  } // constructor

  redirectLinks = []

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * workflow model definition
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.id] - workflow ID
  * @param {integer} [params.version] - model version
  * @param {string} [params.icon] - SVG icon
  * @param {string} [params.md5] - MD5 checksum
  * @param {string} [params.id_generator] - indicates who and how generates id
  * @param {string} [params.sqlview] - definition of SQL query to retrieve read-only workflow
  * @param {string} [params.sqlview_log] - definition of SQL query to retrieve read-only workflow logs
  * @param {string} [params.notifications] - indicates whether to notify on object changes
  * @param {object} [params.fields] - workflow variables
  * @param {object} [params.sql_queries] - SQL queries used for calculation of fields
  * @param {object} [params.forms] - workflow GUI forms
  * @param {string} [params.translations] - workflow translations as CSV
  * @param {string} [params.scripts] - workflow scripts
  * @param {string} [params.styles] - workflow CSS styles
  * @param {array} [params.acls] - access control list
  * @param {object} [params.states] - workflow states and their actions
  */
  async mmModel(sender, params) {
    this.info('mmModel', params)

    await super.mmModel(sender, params)
    this.initQuillDontEraseIdBlot()
    this.initQuillLinkBlot()
    await this.getRedirectLinks()
  }

  async getRedirectLinks() {
    try {
      const { params } = await this.request(this.config.workflow_server, 'mmFetch', {}, { resolve: 'mmData' })
      if (!params.data?.length) return
      this.redirectLinks = params.data
      LANG.createOptions('wiki.redirect_links.', { '': LANG.ZERO_SPACE }, true)
      params.data.forEach(item => {
        LANG.createOptions('wiki.redirect_links.', { [item.anchor_id]: `${item.headline} - ${item.anchor_text}` })
      })
    } catch (e) {
      this.error('Error getting redirect links:', e)
    }
  }

  initQuillLinkBlot() {
    const LinkBlot = Quill.import('formats/link')

    class MyLink extends LinkBlot {
      static create(value) {
        const node = super.create(value)
        value = this.sanitize(value)

        node.setAttribute('href', value)
        node.removeAttribute('target')
        node.removeAttribute('rel')

        return node
      }
    }

    MyLink.tagName = 'A'
    MyLink.blotName = 'link'

    Quill.register(MyLink, true)
  }

  initQuillDontEraseIdBlot() {
    const Parchment = Quill.import('parchment')
    const DontEraseId = new Parchment.Attributor('id', 'id', { scope: Parchment.Scope.INLINE })
    Quill.register(DontEraseId, true)
  }

  /**
   * OVERRIDE of formload to scroll to the anchor in the body
   * @param {string} form_name - the name of the form
   * @param {object} record - the record of the object to select
   * @param {HTMLElement} elForm - the form element
   */
  formload() { // formload
    this.scrollToHashAnchor()
  }

  scrollToHashAnchor() {
    const anchorId = this.findAnchorFromHash(window.location.hash)
    if (!anchorId) {
      if (this.elDetail) {
        const bodyElement = this.elDetail.querySelector('[key="body"]')
        bodyElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
      return
    }

    requestAnimationFrame(() => {
      const selector = `${CSS?.escape ? CSS.escape(anchorId) : anchorId}`
      const target = document.getElementById(selector)

      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    })
  }

  findAnchorFromHash(hash = '') {
    if (!hash.includes('#' + this.listener_id + '/')) return null
    const parts = hash.replace(/^#/, '').split('/')
    const maybeAnchor = parts[2]
    if (maybeAnchor && /^_Toc/i.test(maybeAnchor)) return maybeAnchor
    return null
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * change of window URL hash
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.hash - part of URL hash parametrizing the request
  */
  mmUrlHash(sender, params) {
    super.mmUrlHash(sender, params)
    const hashParts = params.hash.split('/')
    this._urlSuffixFromHash = hashParts[1] ? '/' + hashParts[1] : ''
    this.selectObject2(hashParts[0], true, hashParts[1] ? '/' + hashParts[1] : '')
  } // mmUrlHash

  /**
   * OVERRIDE of selectObject2 to add suffix to the URL hash
   * @param {string} id - the ID of the object to select
   * @param {boolean} list_selected - whether the object is selected in the list
   * @param {string} [suffix] - the suffix to add to the URL hash
   */
  selectObject2(id, list_selected) {
    const suffix = this._urlSuffixFromHash || ''
    id = _.toString(id)

    if (this.window_active) this.el.updateUrlHash('#' + this.listener_id + '/' + id + suffix)
    if (!list_selected) this._urlSuffixFromHash = ''

    const record = _.get(this.elList.getItem(id), 'data')
    if (record) {
      if (list_selected) record._limited = true
      const elItem = this.elList.getItemElement(id)
      if (elItem) {
        setTimeout(() => { elItem.scrollIntoViewIfNeeded() }, 10)
      }
      this.openAction(record, record._form, record._actions)
    } else {
      setTimeout(() => {
        this.elList.scrollTop()
      }, 10)
      this.closeActionView()
    }
  } // selectObject2

  /**
   * OVERRIDE of openActionViewScroll to disable scroll to anchor
   * @param {HTMLElement} elView - the view element
   */
  openActionViewScroll() {
    // override to disable scroll to anchor
  }

  parseAnchorHref(rawHref = '') {
    const trimmed = rawHref.trim()
    if (trimmed === '') return null

    // Already a hash → leave path empty, keep anchor id
    if (trimmed.startsWith('#')) {
      return { href: trimmed, anchorId: trimmed.slice(1) }
    }

    const [pathPart, fragment] = trimmed.split('#')
    if (fragment) {
      return { href: `${pathPart}#${fragment}`, anchorId: fragment }
    }

    // Word imports usually give "_Toc12345" without '#'
    if (/^_Toc/i.test(pathPart)) {
      return { href: `#${pathPart}`, anchorId: pathPart }
    }

    return null
  }

  /**
   * INITIALIZE OF IMPORT FILE WATCHER
   * @param {object} CTX - the context of the object
   */
  initImportFileWatcher = (CTX) => {
    const CTXform = CTX?.form
    if (!CTXform || typeof CTXform.forAll !== 'function') return

    CTXform.forAll('@{import_file}', elInput => {
      if (!elInput) return

      elInput.eventValueChanged = (self, value) => {
        if (!this.docxValidationFile(value)) {
          this.popupError('wf.wiki.invalidFormat')
          return
        }
        this.importDocx({
          params: { import_file: value },
          data: { import_file: value },
          form: CTXform,
        })
      }
    })
  }

  /**
   * INITIALIZE OF REDIRECT LINKS CLICK HANDLER
   * @param {object} CTXform - the form of the object
   */
  iniRedirectLinksClickHandler = (CTX) => {
    const CTXform = CTX?.form
    if (!CTXform || typeof CTXform.forAll !== 'function') return

    const elRedirectLinks = CTXform.find('@{redirect_links}')
    elRedirectLinks.eventValueChanged = (self, value) => {
      const redirectLink = this.redirectLinks.find(link => link.anchor_id === value)

      if (!redirectLink) return
      const link = `<a href="${redirectLink.anchor_id}">${redirectLink.anchor_text}</a>`
      this.pasteHeadlineToBody(link, CTXform)
    }
  }

  pasteHeadlineToBody(html, CTXform) {
    if (!html || !CTXform) return

    const elBodyInput = CTXform.find?.('@{body}')
    const quillElement = elBodyInput?._elValueElement
    const quill = quillElement?._wysiwyg
    if (!quill || typeof quill.insertText !== 'function') return

    const htmlToInsert = _.toString(html).trim()
    if (!htmlToInsert) return

    const selection = quill.getSelection?.(true)
    const insertAt = typeof selection?.index === 'number' ? selection.index : quill.getLength()

    const originalLength = quill.getLength()
    quill.clipboard?.dangerouslyPasteHTML?.(insertAt, htmlToInsert, 'user')
    if (typeof quill.setSelection === 'function') {
      const newLength = quill.getLength()
      const insertedLength = Math.max(newLength - originalLength, 0)
      const silentSource = Quill?.sources?.SILENT || 'silent'
      quill.setSelection(insertAt + insertedLength, 0, silentSource)
    }
  }

  replaceWikiIdToIds(href, id) {
    return href.replace('wikiId', id) || href
  }

  importDocx = (ctx) => {
    const value = ctx?.params?.import_file || ctx?.data?.import_file
    if (!value || !Mammoth) return false

    // Convert data URI to Buffer or ArrayBuffer if needed
    const input = this.convertDataUriToArrayBuffer(value) || ''

    return Mammoth
      .convertToHtml(input)
      .then(result => {
        const html = this.updateQuillDataWithHtml(result.value) // The generated HTML
        // Update the form field with the imported HTML
        if (ctx?.form && typeof ctx.form.updateValues === 'function') {
          ctx.form.updateValues({ body: html })
        }
        return html
      })
      .catch(error => {
        console.error(error)
        return false
      })
  }

  docxValidationFile = (fileData) => {
    if (!fileData) return true

    try {
      if (!fileData.startsWith('data:')) return false
      // Extract MIME type: data:MIME_TYPE;...
      const mimeMatch = fileData.match(/^data:([^;]+);/)
      const allowedMimes = [
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
      ]
      // If we have a MIME type and it is not one of the allowed DOC/DOCX types → invalid
      return (allowedMimes.includes(mimeMatch?.[1] || ''))
    } catch (error) {
      console.error('Error validating file:', error)
      return false
    }
  }

  convertDataUriToArrayBuffer = (value) => {
    let input = value || ''
    if (typeof value === 'string' && value.startsWith('data:')) {
      // Extract base64 part from data URI (format: data:MIME_TYPE;...;base64,BASE64_DATA)
      const base64Index = value.indexOf(';base64,')
      if (base64Index !== -1) {
        const base64String = value.substring(base64Index + 8) // 8 = length of ';base64,'
        // Browser: convert to ArrayBuffer
        try {
          const binaryString = atob(base64String)
          const bytes = new Uint8Array(binaryString.length)
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i)
          }
          input = { arrayBuffer: bytes.buffer }
        } catch (error) {
          console.error('Error converting data URI to ArrayBuffer:', error)
          return Promise.resolve(false)
        }
      } else {
        console.error('Invalid data URI format - missing base64 data')
        return Promise.resolve(false)
      }
    }
    return input
  }

  updateQuillDataWithHtml = (html) => {
    html = this.moveIdToParent(html)
    return this.addPathToLinks(html)
  }

  /**
   * CLEAN ANCHOR TEXT CONTENT mammoth add number of page to the end of the text
   * @param {object} element - the element to clean
   */
  cleanAnchorTextContent = (element) => {
    if (!element) return
    const originalText = element.textContent || ''
    const cleanedText = originalText.replace(/\t\d+$/, '')
    if (cleanedText !== originalText) {
      element.textContent = cleanedText
    }
  }

  addPathToLinks = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const elements = doc.querySelectorAll('a[href]')
    elements.forEach(element => {
      let href = element.getAttribute('href')
      if (href) {
        this.cleanAnchorTextContent(element)

        // If href contains '#_Toc', remove the '#' before '_Toc'
        if (href.includes('#_Toc')) {
          href = href.replace('#_Toc', '_Toc')
        }

        const prefix = window.location.hash.split('/')[0]
        element.setAttribute('href', `${prefix}/${WIKI_ID_PREFIX}/${href}`)
      }
    })
    return doc.body.innerHTML
  }

  moveIdToParent = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const elements = doc.querySelectorAll('a[id]')
    elements.forEach(element => {
      const id = element.getAttribute('id')
      const parent = element.parentElement
      if (id && parent && id.match(/^_Toc/)) {
        parent.setAttribute('id', `${id}`)
        element.removeAttribute('id')
      }
    })
    return doc.body.innerHTML
  }
} // mmodSkWiki
